import { t } from 'i18next';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { Panel, useUpdateNodeInternals } from 'reactflow';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { color, EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { ImageIcon, PlusIcon, SetAsInputIcon, TextIcon } from '@/UI/Icons';
import { FlexCenHorVer, FlexCenVer } from '@/UI/styles';
import { LoadingCircle } from '@/UI/Animations/LoadingCircle';
import {
  figmaNodeTypeToValidInputTypes,
  isValidNode,
  VALID_FIGMA_TEXT_NODES,
} from '@/components/Nodes/FigmaCompositor/utils';
import { getOrCreateFigmaApiClient } from '../FlowComponents/Editor/FigmaCompositor/figmaApiClient';
import { PANEL_WIDTH } from '../consts/ui';
import { useFigmaCompositorView, useNodeData } from '../FlowContext';
import { DrawerHeader } from './DrawerHeader';
import type { ExternalFigmaNode } from '@/components/Nodes/FigmaCompositor/utils';
import type { FigmaCompositorData } from '@/types/node';

// Need to specifically use styled components to generate CSS at load time, not render time,
// and to handle Tooltip rendering and hover state
const LayerRow = styled(FlexCenVer)({
  justifyContent: 'space-between',
  cursor: 'default',
  borderRadius: 4,
  '&:hover': {
    backgroundColor: color.White08_T,
  },
  '&:hover .figma-layer-action': {
    opacity: 1,
  },
});

const LayerAction = styled(Box)({
  flexShrink: 0,
  opacity: 0,
});

const STANDARD_ICON_WIDTH = 16;

interface FigmaLayerTreeProps {
  node: ExternalFigmaNode;
  inputId: string | undefined;
  figmaIdToWeavyInputId: Record<string, string>;
  onSetAsInput: (node: ExternalFigmaNode) => void;
  depth?: number;
}

function FigmaLayerTree({ node, inputId, figmaIdToWeavyInputId, onSetAsInput, depth = 0 }: FigmaLayerTreeProps) {
  const isValid = isValidNode(node);
  const statusColor = inputId || !isValid ? color.White16_T : color.White80_T;
  const Icon = VALID_FIGMA_TEXT_NODES.includes(node.node_type) ? TextIcon : ImageIcon;

  return (
    <>
      <LayerRow sx={{ mx: 1, px: 1, py: 0.5 }}>
        <FlexCenVer sx={{ gap: 1, overflow: 'hidden', pl: depth, minWidth: 0, flex: 1 }}>
          <Icon
            width={STANDARD_ICON_WIDTH}
            height={STANDARD_ICON_WIDTH}
            style={{ flexShrink: 0 }}
            color={statusColor}
          />
          <EllipsisText variant="body-sm-rg" color={statusColor}>
            {node.name}
          </EllipsisText>
        </FlexCenVer>
        {isValid && !inputId && (
          <Tooltip title={t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.MODEL_PROPERTIES.SET_AS_INPUT)}>
            <LayerAction className="figma-layer-action">
              <AppIconButton mode="on-dark" width={24} height={24} onClick={() => onSetAsInput(node)}>
                <SetAsInputIcon />
              </AppIconButton>
            </LayerAction>
          </Tooltip>
        )}
      </LayerRow>
      {node.children.map((child) => (
        <FigmaLayerTree
          key={child.node_id}
          node={child}
          inputId={figmaIdToWeavyInputId[child.node_id]}
          figmaIdToWeavyInputId={figmaIdToWeavyInputId}
          onSetAsInput={onSetAsInput}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

interface FigmaLayersDrawerProps {
  nodeId: string;
}

function FigmaLayersDrawer({ nodeId }: FigmaLayersDrawerProps) {
  const updateNodeInternals = useUpdateNodeInternals();
  const view = useFigmaCompositorView(nodeId);
  const data = useNodeData<FigmaCompositorData>(nodeId);

  const { fileKey, nodeId: figmaNodeId } = data.options;

  const { data: nodeTree, isLoading } = useQuery({
    queryKey: ['figma-compositor-existing-nodes', fileKey, figmaNodeId],
    queryFn: () => getOrCreateFigmaApiClient().getNodes(fileKey!, figmaNodeId!),
    enabled: !!(fileKey && figmaNodeId),
    refetchOnWindowFocus: false,
  });

  const figmaIdToWeavyInputId: Record<string, string> = {};
  for (const inputId of view.getInputIds()) {
    const figmaId = view.getFigmaNodeId(inputId);
    if (figmaId) {
      figmaIdToWeavyInputId[figmaId] = inputId;
    }
  }

  const handleAddInputHandleNewLayer = () => {
    view.addNodeInputHandle();
    updateNodeInternals(nodeId);
  };

  const handleSetAsInput = (node: ExternalFigmaNode) => {
    const validTypes = figmaNodeTypeToValidInputTypes(node.node_type);
    view.addNodeInputHandle(node.name, node.node_id, validTypes);
    updateNodeInternals(nodeId);
  };

  return (
    <Panel
      position="top-right"
      style={{
        height: '100%',
        width: `${PANEL_WIDTH}px`,
        top: '0px',
        right: '0px',
        margin: 0,
      }}
      className="slide-right-enter"
    >
      <Box
        sx={{
          height: '100%',
          overflow: 'hidden auto',
          backgroundColor: color.Black92,
          borderLeft: `1px solid ${EL_COLORS.BoxBorder}`,
        }}
      >
        <DrawerHeader />
        <FlexCenVer sx={{ m: 2, justifyContent: 'space-between', pointerEvents: 'none' }}>
          <Typography variant="body-sm-rg" color={color.White64_T}>
            {t(I18N_KEYS.FIGMA_COMPOSITOR.LAYERS)}
          </Typography>
          <AppIconButton
            mode="on-dark"
            width={16}
            height={16}
            onClick={handleAddInputHandleNewLayer}
            sx={{ pointerEvents: 'auto' }}
          >
            <PlusIcon width={STANDARD_ICON_WIDTH} height={STANDARD_ICON_WIDTH} />
          </AppIconButton>
        </FlexCenVer>
        {isLoading && (
          <FlexCenHorVer sx={{ py: 4 }}>
            <LoadingCircle size={24} color="white" />
          </FlexCenHorVer>
        )}
        {nodeTree && (
          <FigmaLayerTree
            key={nodeTree.node_id}
            node={nodeTree}
            inputId={figmaIdToWeavyInputId[nodeTree.node_id]}
            figmaIdToWeavyInputId={figmaIdToWeavyInputId}
            onSetAsInput={handleSetAsInput}
          />
        )}
      </Box>
    </Panel>
  );
}

export default FigmaLayersDrawer;
